import { forwardRef } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { platformProps } from "../../lib";
import type {
  TabsContentPrimitiveProps,
  TabsListPrimitiveProps,
  TabsRootPrimitiveProps,
  TabsTriggerPrimitiveProps,
} from "./types";

/** Ref types, resolved per platform like the components. */
export type TabsRootRef = HTMLDivElement;
export type TabsListRef = HTMLDivElement;
export type TabsTriggerRef = HTMLButtonElement;
export type TabsContentRef = HTMLDivElement;

/**
 * Tabs Root - Web implementation (Radix)
 *
 * Unstyled primitive. Styling is applied by the molecule layer via className.
 */
const TabsRoot = forwardRef<TabsRootRef, TabsRootPrimitiveProps>(
  (
    {
      value,
      onValueChange,
      orientation,
      dir,
      activationMode,
      className,
      children,
      testID,
      ...injected
    },
    ref,
  ) => (
    <TabsPrimitive.Root
      ref={ref}
      value={value}
      onValueChange={onValueChange}
      orientation={orientation}
      dir={dir}
      activationMode={activationMode}
      className={className}
      {...platformProps({ testID, slot: "tabs" })}
      {...injected}
    >
      {children}
    </TabsPrimitive.Root>
  ),
);

TabsRoot.displayName = TabsPrimitive.Root.displayName;

/**
 * Tabs List - Web implementation (Radix)
 *
 * Unstyled primitive. Wraps the tab triggers.
 */
const TabsList = forwardRef<TabsListRef, TabsListPrimitiveProps>(
  ({ className, children, testID, accessibilityLabel, ...injected }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      aria-label={accessibilityLabel}
      className={className}
      {...platformProps({ testID, slot: "tabs-list" })}
      {...injected}
    >
      {children}
    </TabsPrimitive.List>
  ),
);

TabsList.displayName = TabsPrimitive.List.displayName;

/**
 * Tabs Trigger - Web implementation (Radix)
 *
 * Unstyled primitive. Represents a single tab button.
 */
const TabsTrigger = forwardRef<TabsTriggerRef, TabsTriggerPrimitiveProps>(
  ({ value, disabled, className, children, testID, accessibilityLabel, ...injected }, ref) => (
    <TabsPrimitive.Trigger
      ref={ref}
      value={value}
      disabled={disabled}
      aria-label={accessibilityLabel}
      className={className}
      {...platformProps({ testID, slot: "tabs-trigger" })}
      {...injected}
    >
      {children}
    </TabsPrimitive.Trigger>
  ),
);

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

/**
 * Tabs Content - Web implementation (Radix)
 *
 * Unstyled primitive. Renders the content for a given tab value.
 */
const TabsContent = forwardRef<TabsContentRef, TabsContentPrimitiveProps>(
  ({ value, forceMount, className, children, testID, ...injected }, ref) => (
    <TabsPrimitive.Content
      ref={ref}
      value={value}
      forceMount={forceMount}
      className={className}
      {...platformProps({ testID, slot: "tabs-content" })}
      {...injected}
    >
      {children}
    </TabsPrimitive.Content>
  ),
);

TabsContent.displayName = TabsPrimitive.Content.displayName;

export { TabsRoot, TabsList, TabsTrigger, TabsContent };
